import * as React from 'react';
import type { PrimitiveType } from 'react-intl';
import { useIntl } from 'react-intl';
import type { TDispatch } from 'Store/index';
import { useAppSelector, useAppDispatch as useDispatch, useAppSelector as useSelector } from 'Store/index';
import colors from '../../../config/theme/colors';
import InviteForm from './Form/Invite';
import TaskListForm from './Form/TaskList';
import MyServiceHistory from './HistoryRowData';
import SurveyForm from './Form/Survey';
import { actions } from 'Store/myapp/actions';
import { actions as patientActions } from 'Store/patient/actions';
import DocumentHeader from 'Components/DocumentHeader';
import DocumentWrapper from 'Components/DocumentWrapper';
import Toolbar from 'Components/Toolbar';
import {
  makeDocumentHeader,
  makeDocumentEditingInfotext,
  makeDocumentInfotext,
  hasRequiredTasklistData,
} from '../util';
import browserLanguage from 'Utility/browserLanguage';
import { filterDocumentsByType } from 'Utility/documentHandling';
import { fetchInformantBySsn, linkPatientToInformant } from 'Store/patient/apiFetchers';
import { reduxActions } from 'Store/patient';
import { isDocumentType, isMyDocumentType } from 'neuro-schemas';
import { arrayOfAll, isJSObjectType } from 'neuro-utils';

const isPartialTasklist = (data: unknown): data is Partial<ITaskList> => {
  const keys = arrayOfAll.keys<ITaskList>()([
    'id',
    'task_kind',
    'issuer_org_id',
    'template_id',
    'description',
    'recurrence',
    'informant_id',
    'appointment_id',
    'tasks',
    'start_inclusive',
    'end_exclusive',
    'timezone',
    'removed_at',
    'alerts_after_start',
    'reminders_before_end',
    'platform',
    'newInformant',
  ]);
  if (!isJSObjectType(data)) return false;
  if (Object.keys(data).some(key => !(keys as string[]).includes(key))) return false;
  return true;
};

export type MyServiceContextType = '_invite' | '_tasklist' | TMyDocumentType;

type MyServiceContextTypeDataMap = {
  _invite: Partial<IInvite>;
  _tasklist: Partial<ITaskList>;
};

interface IEditing<Type extends MyServiceContextType> {
  type: Type;
  /** nice spaghetti ;-) */
  data: Type extends '_invite'
    ? MyServiceContextTypeDataMap[Type]
    : Type extends '_tasklist'
      ? MyServiceContextTypeDataMap[Type]
      : Type extends TMyDocumentType
        ? TDocumentDataMap[Type] & Partial<IControlProps> // any control props can exist?
        : never;
}

export const MyServiceContext = React.createContext<IMyServiceContext<MyServiceContextType> | null>(null);

type FMFn = (
  m: string,
  n?:
    | string
    | number
    | Record<
        string,
        | PrimitiveType
        | React.JSX.Element
        | ((chunks: string | Array<string | React.JSX.Element> | React.JSX.Element) => React.JSX.Element | string)
      >,
) => string;

export interface IMyServiceContext<Type extends MyServiceContextType> {
  fm: FMFn;
  editing: IEditing<Type> | null;
  viewing: IEditing<Type> | null;
  setEditingObj: (ed: IEditing<Type>) => void;
  setEditingData: ((data: IEditing<Type>['data']) => void) | null;
  setViewingObj: (ed: IEditing<Type> | null) => void;
}

const saveContactInfo = (
  data: Array<INeuroContactInfo | INeuroUIContactInfo>,
  oldData: INeuroContactInfo[],
  dispatch: TDispatch,
): void => {
  const newContacts = data.filter(d => !oldData.some(od => d.type === od.type && d.value === od.value));
  if (newContacts.length > 0) {
    newContacts.forEach(n => {
      dispatch(patientActions.createPatientContactInfo(n));
    });
  }
};

const MyService = ({ documents }: IOwnProps): React.JSX.Element => {
  const { formatMessage } = useIntl();

  const fm: FMFn = React.useCallback(
    (id, n) => {
      if (n && typeof n === 'object') return formatMessage({ id }, n as Record<string, any>);
      return formatMessage({ id }, { n });
    },
    [formatMessage],
  );

  const [selectedTab, setSelectedTab] = React.useState<number>(0);

  const [editing, setEditing] = React.useState<IEditing<MyServiceContextType> | null>(null);
  const [viewing, setViewing] = React.useState<IEditing<MyServiceContextType> | null>(null);
  const [inviteErrors, setInviteErrors] = React.useState<Array<{ method: ContactInfoType; statusCode: number }>>([]);

  const setEditingData = (data: IEditing<MyServiceContextType>['data']) => {
    if (editing) setEditing({ ...editing, data }); // TODO should this merge data instead of overwriting it?
  };

  const cancelEdit = () => {
    setEditing(null);
  };

  const dispatch = useDispatch();

  const locale = useAppSelector(s => s.settings.userSettings.uiLanguage) || browserLanguage;
  const patientId = useSelector(s => s.patient.data?.id);
  const orgId = useSelector(s => s.session.data?.orgid);
  const platform = useSelector(s => s.session.platforms?.selected);
  // const patientMySQStatus = useSelector(s => s.myapp.mysqUserId);
  const sentInvites = useSelector(s => s.myapp.sentInvites);
  // const taskLists = useSelector(s => s.myapp.taskLists);
  const taskStimulusFormSettings = useSelector(s => s.settings.orgSettings.taskUiSettings);
  const sortedAndMergedDocuments = useSelector(s => s.documents.sortedAndMergedDocuments) ?? [];

  const patientContactInfo = useSelector((s: { patient: IPatientStore }) => s.patient.contactInfo);

  const [inviteContactInfo, setInviteContactInfo] = React.useState<
    Array<INeuroContactInfo | INeuroUIContactInfo> | undefined
  >(undefined);

  const hasContactInfo = Array.isArray(inviteContactInfo) && inviteContactInfo.length > 0;

  // const data = editing?.data ?? {};

  // wtf is happening in here? TODO
  // const firstInviteUpcoming =
  //   sentInvites.length === 0 &&
  //   taskLists.filter(tl => tl.task_kind === 'PROM' || (!tl.task_kind && !tl.informant_id)).length > 0;

  // const hasSomeTaskListData = Boolean(
  //   editing?.data &&
  //   (data.title ||
  //     data.deadline ||
  //     data.recurringTaskDeadline ||
  //     data.weeklySurveyInterval ||
  //     data.sendDate ||
  //     (Array.isArray(data.tasks) && data.tasks.length > 0) ||
  //     data.description),
  // );

  // const hasRequiredTaskListData = Boolean(
  //   editing?.data as ITaskList &&
  //   data.template_id &&
  //   data.deadline &&
  //   (!data.recurring || data.recurringTaskDeadline || data.weeklySurveyInterval) &&
  //   data.sendDate &&
  //   data.description &&
  //   platform &&
  //   ((taskStimulusFormSettings?.[platform][data.template_id]?.taskKind === 'IROM' && data.informant_d) ||
  //     taskStimulusFormSettings?.[platform][data.template_id]?.taskKind === 'PROM'),
  // );

  const handleSaveInvite = async (): Promise<unknown> => {
    if (!editing) return;
    const tasklistData = isPartialTasklist(editing.data) ? editing.data : null;

    const newTasklist = () => {
      if (
        tasklistData?.tasks &&
        patientId &&
        orgId &&
        (tasklistData.end_exclusive || (tasklistData.recurrence?.recurring && tasklistData.recurrence.first_deadline))
      ) {
        actions
          .newTasklist(tasklistData as ITaskList, dispatch)
          .then(res => {
            if (res) setEditing(null);
          })
          .catch(() => setEditing(null));
      }
    };

    if (Array.isArray(inviteContactInfo)) {
      // Invite and/or Tasklist

      // Invite shall not be sent separately if tasklist data is present
      if (tasklistData && hasContactInfo) {
        if (inviteContactInfo) saveContactInfo(inviteContactInfo, patientContactInfo?.contacts ?? [], dispatch);
        newTasklist();
      } else {
        Promise.all(
          inviteContactInfo.map(info => {
            const carrier = info.value;
            const medium: Uppercase<INeuroContactInfo['type']> = info.type.toUpperCase() as Uppercase<
              INeuroContactInfo['type']
            >;
            if (carrier && medium && orgId) {
              return actions.newInvite(carrier, medium, orgId, dispatch, locale); // TODO WHY IS USER'S LOCALE PASSED HERE INSTEAD OF RECIPIENT'S LANGUAGE?
            } else return;
          }),
        )
          .then(responses => {
            responses.forEach((status, i) => {
              const method = inviteContactInfo[i]?.type;
              if (method && status && status !== 200) {
                const otherErrors = inviteErrors.filter(err => err.method !== method);
                setInviteErrors([...otherErrors, { method, statusCode: status }]);
              } else {
                const otherErrors = inviteErrors.filter(err => err.method !== method);
                setInviteErrors(otherErrors);
              }
            });

            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            if (inviteContactInfo) saveContactInfo(inviteContactInfo, patientContactInfo?.contacts ?? [], dispatch);
            // If tasklist data is present, send new tasklist after invites have been sent
            newTasklist();
          })
          .catch(() => {
            setEditing(null);
          });
      }
    }
    return;
  };

  const handleSaveTasklist = async (): Promise<unknown> => {
    if (!editing) return;
    if (!patientId || !orgId) return; // log?
    if (!isPartialTasklist(editing.data)) return; // log?

    const tasklist = editing.data;
    const { newInformant } = tasklist;

    let newlyCreatedInformantId: string | undefined;

    if (newInformant) {
      const { ssn, ssnLocalization } = newInformant;
      if (!ssn || !ssnLocalization) return; // ??
      const alreadyExistingInformant = await fetchInformantBySsn(ssn, ssnLocalization);
      if (alreadyExistingInformant) {
        // link the existing informant to patient
        await linkPatientToInformant(alreadyExistingInformant.id);
        dispatch(reduxActions.createInformant({ ...alreadyExistingInformant, isActive: true }));
        newlyCreatedInformantId = alreadyExistingInformant.id;
      } else {
        const informant: Omit<IInformant, 'id'> = {
          ssn,
          ssnLocalization,
          lastNames: newInformant.lastName ?? null,
          firstNames: newInformant.firstName ?? null,
          phoneNumber: newInformant.phoneNumber ?? null,
          email: newInformant.email ?? null,
          removed: null,
          mysqUserId: null,
          source: 'neuro-ui',
          kind: null,
          isActive: true,
        };
        newlyCreatedInformantId = (await dispatch(patientActions.createPatientInformant(informant))) ?? undefined;
        // TODO if informant creation fails -> return and display an error to user?
      }
    }

    // idk what happens here TODO
    if (inviteContactInfo) {
      saveContactInfo(inviteContactInfo, patientContactInfo?.contacts ?? [], dispatch);
    }

    const taskListDataToPost = {
      ...tasklist,
      informant_id: newlyCreatedInformantId ?? tasklist.informant_id,
      newInformant: undefined,
    } as SafeOmit<ITaskList, 'id'> & { id?: string };

    if (taskListDataToPost.id) {
      actions
        .updateTasklist(taskListDataToPost as ITaskList, dispatch)
        .then(res => {
          // TODO Errors here should be shown to the user i guess?
          if (res) setEditing(null);
        })
        .catch(() => {
          setEditing(null);
        });
    } else {
      actions
        .newTasklist(taskListDataToPost as ITaskList, dispatch)
        .then(res => {
          if (res) setEditing(null);
        })
        .catch(() => {
          setEditing(null);
        });
    }
    return;
  };

  const handleSaveSurvey = async (): Promise<unknown> => {
    if (!editing) return;
    if (!isMyDocumentType(editing.type)) return; // log?
    const surveyData = editing.data;

    const id = surveyData && '_id' in surveyData ? surveyData._id : undefined;
    if (id) {
      actions
        .putNewCommit(editing.type, id, surveyData, dispatch)
        .then(() => setEditing(null))
        .catch(() => setEditing(null)); // display errors to user? TODO
    } else {
      actions
        .putNewMyDocument(editing.type, surveyData, dispatch)
        .then(() => setEditing(null))
        .catch(() => setEditing(null)); // display errors to user? TODO
    }
    return;
  };

  const sendAndSave = async (): Promise<void> => {
    if (!editing) return;
    if (editing.type === '_invite') await handleSaveInvite();
    else if (editing.type === '_tasklist') await handleSaveTasklist();
    else if (isMyDocumentType(editing.type)) await handleSaveSurvey();
    setEditing(null); // this seems to be done also in the functions above but idk
  };

  const onChangeInviteContactInfo = (values: TOnChangeValues): void => {
    const name = Object.keys(values)[0];
    if (!name) return;
    const value = values[name];
    if ((name === 'smsList' || name === 'emailList') && Array.isArray(value)) {
      const oldValues = inviteContactInfo
        ?.filter(ci => (name === 'emailList' ? 'email' : 'sms') === ci.type)
        .map(ci => ci.value);
      const newValue = value.find(v => !oldValues?.includes(v));
      if (newValue) {
        setInviteContactInfo([
          ...(inviteContactInfo ?? []),
          {
            type: name === 'emailList' ? 'email' : 'sms',
            value: newValue,
            contactAllowed: true,
            rank: null,
            use: 'unknown',
          },
        ]);
      } else if (value.length < (oldValues?.length ?? 0)) {
        const removedValue = oldValues?.find(ov => !value.includes(ov));
        if (removedValue) {
          setInviteContactInfo((inviteContactInfo ?? []).filter(ci => ci.value !== removedValue));
        }
      }
    }
  };

  const saveButtonDisabled = (): boolean => {
    switch (editing?.type) {
      case '_tasklist':
        return !hasRequiredTasklistData({ tasklist: editing.data as Partial<ITaskList> });
      case '_invite': {
        // Not the first invite, only contact info is required
        if (sentInvites.length > 0) return !hasContactInfo;

        // Required task list fields need to exist if some have been entered
        // wtf does this even mean^^? TODO
        // if (hasSomeTaskListData) return !hasRequiredTaskListData || !hasContactInfo;

        // Only contact info is required
        return !hasContactInfo;
      }
      default:
        // this handles all the document types -> never disabled?
        return false;
    }
  };

  let epilepsySeizuresNoNameByPatient = false;

  if (platform === 'epilepsy') {
    const seizureTypeDocs = filterDocumentsByType(sortedAndMergedDocuments, ['seizureType']);
    // should this be .some or .every? TODO
    epilepsySeizuresNoNameByPatient = seizureTypeDocs.some(std => !std.nameByPatient);
  }

  const contextValue: IMyServiceContext<MyServiceContextType> = React.useMemo(
    () => ({
      fm,
      editing,
      viewing,
      setEditingObj: setEditing,
      setEditingData,
      setViewingObj: setViewing,
    }),
    [fm, editing, viewing, setEditing, setEditingData, setViewing],
  );

  return (
    <>
      <Toolbar
        current="myService"
        editing={editing ? 'editing' : undefined}
        view={viewing ? { viewing: 'true', endView: () => () => setViewing(null) } : undefined}
        cancelButtonCallback={cancelEdit}
        cancelDraft={() => () => null}
        saveDraft={() => () => null}
        saveButtonCallback={sendAndSave}
        saveButtonDisabled={saveButtonDisabled()}
      />
      <MyServiceContext.Provider value={contextValue}>
        <DocumentWrapper>
          {!editing && !viewing ? (
            <div style={{ color: colors.primary, fontWeight: 600, lineHeight: '1.6rem' }}>
              {fm('myService.appName')}
            </div>
          ) : undefined}
          <DocumentHeader
            name={'myService'}
            headerId={
              editing
                ? makeDocumentHeader(editing.type)
                : viewing
                  ? makeDocumentHeader(viewing.type)
                  : platform === 'epilepsy'
                    ? 'myService.titleEpilepsy'
                    : 'myService.title'
            }
            headerWidth={10}
            editing={undefined}
            infoText={
              editing
                ? makeDocumentEditingInfotext(editing.type, !(sentInvites.length === 0) || platform === 'epilepsy')
                : makeDocumentInfotext(platform, {
                    epilepsySeizuresNoNameByPatient,
                    docType: isDocumentType(viewing?.type) ? viewing.type : undefined,
                    docData: viewing?.data,
                    locale,
                  })
            }
          />
          {editing && editing.type === '_invite' ? (
            <InviteForm
              inviteContactInfo={inviteContactInfo}
              setInviteContactInfo={setInviteContactInfo}
              onChangeInviteContactInfo={onChangeInviteContactInfo}
            />
          ) : editing && editing.type === '_tasklist' ? (
            <TaskListForm
              templateSettingsByPlatform={taskStimulusFormSettings ?? {}}
              inviteContactInfo={inviteContactInfo}
              setInviteContactInfo={setInviteContactInfo}
              onChangeInviteContactInfo={onChangeInviteContactInfo}
            />
          ) : editing && typeof editing.type === 'string' ? (
            <SurveyForm documents={documents} />
          ) : undefined}

          {viewing && typeof viewing.type === 'string' ? <SurveyForm documents={documents} /> : undefined}

          {!(editing || viewing) ? (
            <MyServiceHistory selectedTab={selectedTab} setSelectedTab={setSelectedTab} inviteErrors={inviteErrors} />
          ) : undefined}
        </DocumentWrapper>
      </MyServiceContext.Provider>
    </>
  );
};

export const useMyServiceContext = <Type extends MyServiceContextType>(): IMyServiceContext<Type> => {
  const context = React.useContext(MyServiceContext);
  if (!context) throw new Error('MyServiceContext used outside of its provider');
  return context as unknown as IMyServiceContext<Type>;
};

interface IOwnProps {
  documents: Array<IDocumentWithTypeV2<'doctorsOrders' | 'ninmtTreatmentPeriod' | 'symptomsAndLocations'>>;
}

export default MyService;
